import * as React from 'react';
import { HybridLinkProps } from '@westwing/appshell_types';
import { HybridLink } from '@ids/appshell/RoutingHelper';
import { useAppshellMeta } from '@ids/appshell/hooks';

interface NativeLinkProps extends React.HTMLAttributes<HTMLDivElement> {
    onNativeAppClick?: () => void;
}

const NativeAppLink = ({
    onNativeAppClick,
    onClick,
    children,
    ...restProps
}: React.PropsWithChildren<NativeLinkProps>) => {
    const handleClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (typeof onClick === 'function') {
            onClick(e);
        }
        if (typeof onNativeAppClick === 'function') {
            onNativeAppClick();
        }
    };

    return (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div onClick={handleClick} data-testid="native-link" {...restProps}>
            {children}
        </div>
    );
};

interface WebLinkProps extends HybridLinkProps {
    onWebClick?: () => void;
}

const WebLink = ({ onWebClick, onClick, children, ...hybridLinkProps }: React.PropsWithChildren<WebLinkProps>) => {
    const handleClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        if (typeof onClick === 'function') {
            onClick(e);
        }
        if (typeof onWebClick === 'function') {
            onWebClick();
        }
    };

    return (
        <HybridLink {...hybridLinkProps} testId="hybrid-link" onClick={handleClick}>
            {children}
        </HybridLink>
    );
};

export type LinkProps = NativeLinkProps & HybridLinkProps & { onWebClick?: () => void };

const Link = ({
    children,
    onClick,
    onNativeAppClick,
    onWebClick,
    ...restProps
}: React.PropsWithChildren<LinkProps>) => {
    const { isApp } = useAppshellMeta();

    const nativeLinkProps = {
        onClick,
        onNativeAppClick,
        className: restProps.className,
    };

    const hybridLinkProps = {
        ...restProps,
        onClick,
        onWebClick,
    };

    return isApp ? (
        <NativeAppLink {...nativeLinkProps}>{children}</NativeAppLink>
    ) : (
        <WebLink {...hybridLinkProps}>{children}</WebLink>
    );
};

export default Link;
